import React, { useState, useCallback, useEffect, memo } from 'react';
import { SortableContainer } from 'react-sortable-hoc';
import { ContextMenu, ContextMenuTrigger, MenuItem } from 'react-contextmenu';
import arrayMove from '../../../../utils/arrayMove';

import { SortableItem } from '../../../decorators/SortableItem';
import uniqueId from '../../../../utils/uniqueId';
import { isWrapped, unwrap } from '../../../../utils/ChangeSpy';

import style from './style.module.css';

const ContactItem = ({ children, ...rest }) => (
  <div {...rest} className={style.item}>
    {children}
  </div>
);

const SortableContactItem = SortableItem(ContactItem);

const ContextMenuWrapper = ({ onDelete, onCopy, onEdit }) => (type, value, index) => content => {
  const id = uniqueId();

  return (
    <div>
      <ContextMenuTrigger holdToDisplay={-1} id={id}>
        {content}
      </ContextMenuTrigger>

      <ContextMenu id={id}>
        <MenuItem data={{ index, type, value }} onClick={onEdit}>
          <i className="fa fa-pencil" /> Edit
        </MenuItem>
        <MenuItem data={{ index, type, value }} onClick={onCopy}>
          <i className="fa fa-save" /> Copy
        </MenuItem>
        <MenuItem data={{ index, type, value }} onClick={onDelete}>
          <i className="fa fa-remove" /> Delete
        </MenuItem>
      </ContextMenu>
    </div>
  );
};

const sortItems = (first, second) => {
  if (first.priority < second.priority) return -1;
  if (first.priority > second.priority) return 1;

  return 0;
};

const prepareItems = items => items.map((item, index) => ({ ...item, channelIndex: index })).sort(sortItems);

const ItemSorter = ({
  className,
  name,
  readOnly,
  verifyField = 'isVerify',
  isVisibleVerifyIcon,
  items: itemsProp,
  reorderChannels,
  onDelete,
  onCopy,
  onEditChannel,
}) => {
  const [items, setItems] = useState(() => prepareItems(itemsProp || []));

  useEffect(() => {
    setItems(prepareItems(itemsProp || []));
  }, [itemsProp]);

  const handleClick = useCallback((event, item) => {
    event.stopPropagation();
    event.preventDefault();
    if (item) {
      window.location = `mailto:${encodeURI(item.email.value)}`;
    }
  }, []);

  const handleSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      setItems(prevItems => {
        const newItems = arrayMove(prevItems, oldIndex, newIndex);

        if (reorderChannels) {
          // when users stop dragging
          reorderChannels(newItems);
        }

        return newItems;
      });
    },
    [reorderChannels],
  );

  const contactsValues = [];

  items.forEach((item, index) => {
    const itemValue = name === 'phones' ? item.phone.value : item.email.value;
    const type = item.type.value;
    const itemKey = item.id ?? `${name}_${item.channelIndex}`;

    if (readOnly) {
      contactsValues.push(
        <strong key={itemKey}>
          {itemValue} {type ? `(${type})` : null}
        </strong>,
      );
    } else {
      let icon = null;

      if (isVisibleVerifyIcon) {
        if ((!isWrapped(item[verifyField]) && item[verifyField]) || unwrap(item[verifyField])) {
          icon = <i className="text-success fa fa-check" />;
        }
      }

      const content = ContextMenuWrapper({
        onDelete: event => onDelete(event, name, item.channelIndex),
        onCopy,
        onEdit: () => onEditChannel(name, item.channelIndex),
      })(
        name,
        itemValue,
        index,
      )(
        <div className={style.sorter}>
          <strong className="truncate-text w-auto">
            <a
              href="#"
              onClick={event => {
                handleClick(event, name === 'emails' ? item : null);
              }}
            >
              {itemValue} {type ? `(${type})` : null}
            </a>
          </strong>
          {icon}
        </div>,
      );

      contactsValues.push(
        <SortableContactItem key={itemKey} index={index}>
          {content}
        </SortableContactItem>,
      );
    }
  });

  return (
    <div className={className}>
      <SortableContactList contacts={contactsValues} distance={1} lockAxis="y" onSortEnd={handleSortEnd} />
    </div>
  );
};

const SortableContactList = SortableContainer(({ contacts }) => <ul className={`pl0 ${style.list}`}>{contacts}</ul>);

export default memo(ItemSorter);
